<template>
    <!-- Inline mode: a wrapping row of pills (e.g. under the tour detail title) -->
    <div v-if="inline && inlinePromos.length" class="flex flex-wrap gap-2">
        <span
            v-for="promo in inlinePromos"
            :key="promo.id"
            v-tooltip.top="promo.terms"
            class="promo-badge gap-1 inline-flex w-max items-center px-3 py-1 text-xs font-semibold uppercase leading-tight tracking-wide ring-1 ring-black/10"
            :style="badgeStyle(promo)"
        >
            <span>
                {{ promo.label ?? promo.name }}
            </span>
        </span>
    </div>

    <!-- Overlay mode: corner badges layered over the card image -->
    <template v-else-if="!inline">
        <div
            v-for="group in groups"
            :key="group.key"
            class="absolute z-10 flex max-w-[85%] flex-col gap-1.5 p-2.5"
            :class="group.class"
        >
            <span
                v-for="promo in group.items"
                :key="promo.id"
                v-tooltip.top="promo.terms"
                class="promo-badge gap-1 inline-flex w-max items-center px-3 py-1 text-xs font-semibold uppercase leading-tight tracking-wide ring-1 ring-black/10 backdrop-blur-[2px]"
                :style="badgeStyle(promo)"
            >
                <span>
                    {{ promo.label ?? promo.name }}
                </span>
            </span>
        </div>
    </template>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { Tour, Promotion, PromotionPlacement } from "@/types";

const props = withDefaults(
    defineProps<{
        tour: Tour;
        // inline = a wrapping row of pills (tour detail page);
        // default = corner badges layered over a card image
        inline?: boolean;
    }>(),
    { inline: false },
);

const byPriority = (a: Promotion, b: Promotion) =>
    (a.priority ?? 0) - (b.priority ?? 0);

// Eye-grabbing badge: API colour plus a tinted glow so it pops off the photo.
const badgeStyle = (promo: Promotion) => ({
    backgroundColor: promo.color,
    color: promo.text_color,
    "--promo-color": promo.color,
});

// Only promotions that live on the image (the corner badges).
// "price"-placed promotions are handled by Price.vue instead.
const overlayPromos = computed<Promotion[]>(() =>
    (props.tour.promotions ?? [])
        .filter((p) => p.placement?.startsWith("image_"))
        .slice()
        .sort(byPriority),
);

// Inline mode shows every visible promotion (anything but the price-row tooltip).
const inlinePromos = computed<Promotion[]>(() =>
    (props.tour.promotions ?? [])
        .filter((p) => p.placement !== "price")
        .slice()
        .sort(byPriority),
);

const at = (placement: PromotionPlacement) =>
    overlayPromos.value.filter((p) => p.placement === placement);

const groups = computed(() =>
    [
        {
            key: "tl",
            class: "top-0 left-0 items-start",
            items: at("image_top_left"),
        },
        {
            key: "tr",
            class: "top-0 right-0 items-end",
            items: at("image_top_right"),
        },
        {
            key: "bl",
            class: "bottom-0 left-0 items-start",
            items: at("image_bottom_left"),
        },
        {
            key: "br",
            class: "bottom-0 right-0 items-end",
            items: at("image_bottom_right"),
        },
    ].filter((group) => group.items.length),
);
</script>

<style scoped>
.promo-badge {
    position: relative;
    border-radius: 9999px;
    /* coloured glow tied to the badge's own colour so it pops off the photo */
    box-shadow:
        0 2px 6px rgba(0, 0, 0, 0.25),
        0 0 0 0 color-mix(in srgb, var(--promo-color, #000) 60%, transparent);
    transition:
        transform 0.2s ease,
        box-shadow 0.3s ease;
    animation: promo-pop 0.4s ease-out both;
}

/* gentle attention pulse, looping but unobtrusive */
.promo-badge::after {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: inherit;
    box-shadow: 0 0 0 0 color-mix(in srgb, var(--promo-color, #000) 70%, transparent);
    animation: promo-pulse 2.4s ease-out infinite;
    pointer-events: none;
}

.item:hover .promo-badge {
    transform: translateY(-1px) scale(1.04);
    box-shadow:
        0 8px 18px rgba(0, 0, 0, 0.3),
        0 0 14px 2px color-mix(in srgb, var(--promo-color, #000) 55%, transparent);
}

@keyframes promo-pop {
    from {
        opacity: 0;
        transform: scale(0.85);
    }
    to {
        opacity: 1;
        transform: scale(1);
    }
}

@keyframes promo-pulse {
    0% {
        box-shadow: 0 0 0 0 color-mix(in srgb, var(--promo-color, #000) 55%, transparent);
    }
    70%,
    100% {
        box-shadow: 0 0 0 8px transparent;
    }
}

@media (prefers-reduced-motion: reduce) {
    .promo-badge,
    .promo-badge::after {
        animation: none;
    }
}
</style>
